// src/components/HeroSection.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import publicData from '../../../data/publicData.json';
import background1 from '../../../assets/back1.jpg';
import background2 from '../../../assets/back2.jpg';

// Add useWindowSize hook
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const HeroSection = ({ theme = 'dark' }) => {
  const { width } = useWindowSize();
  const isMobile = width < 768; // Define mobile breakpoint
  const sliderRef = useRef(null);
  const servicesRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);

  const { hero, services } = publicData;
  const totalSlides = services.items.length;
  const slidesToShow = 3;

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const moveSlide = () => {
      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
      const slideWidth = slider.clientWidth;

      if (slider.scrollLeft >= maxScrollLeft) {
        slider.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        slider.scrollBy({ left: slideWidth, behavior: 'smooth' });
      }
    };

    const interval = setInterval(moveSlide, 3000);
    return () => clearInterval(interval);
  }, [sliderRef]);

  useEffect(() => {
    const play = () => {
      if (isAutoPlaying && servicesRef.current) {
        const nextSlide = (currentSlide + 1) % Math.ceil(totalSlides / slidesToShow);
        scrollToSlide(nextSlide);
      }
    };

    autoPlayRef.current = setInterval(play, 5000);
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [currentSlide, isAutoPlaying, totalSlides, slidesToShow]);

  const scrollToSlide = (slideIndex) => {
    const container = servicesRef.current;
    if (!container) return;

    const slideWidth = container.clientWidth;
    const scrollPosition = slideWidth * slideIndex;
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    setCurrentSlide(slideIndex);
  };

  useEffect(() => {
    const container = servicesRef.current;
    if (!container) return;

    const handleScrollEvent = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const currentScroll = container.scrollLeft;
      
      setShowLeftArrow(currentScroll > 0);
      setShowRightArrow(currentScroll < maxScroll - 10);
      
      // Update current slide based on scroll position
      const slideWidth = container.clientWidth;
      const newSlide = Math.round(currentScroll / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    };

    container.addEventListener('scroll', handleScrollEvent);
    return () => container.removeEventListener('scroll', handleScrollEvent);
  }, [currentSlide, servicesRef]);

  const scrollServices = (direction) => {
    if (!servicesRef.current) return;

    const container = servicesRef.current;
    const slideWidth = container.clientWidth;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - slideWidth
      : container.scrollLeft + slideWidth;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    // Pause auto-play temporarily when manually navigating
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Add this before MobileHeroSection component
  const autoSlideMobile = () => {
    if (!servicesRef.current) return;
    
    const container = servicesRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const currentScroll = container.scrollLeft;
    
    if (currentScroll >= maxScroll) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
    }
  };

  // Add mobile auto-slide effect
  useEffect(() => {
    if (!isMobile || !isAutoPlaying) return;

    const interval = setInterval(autoSlideMobile, 3000);
    return () => clearInterval(interval);
  }, [isMobile, isAutoPlaying]);

  // Update mobile carousel click handler
  const handleMobileSlideClick = (index) => {
    if (!servicesRef.current) return;
    
    const slideWidth = window.innerWidth * 0.85; // 85vw
    servicesRef.current.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth'
    });
    setCurrentSlide(index);
    
    // Pause auto-play temporarily when manually navigating
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000); 
  };

  // Add scroll handler for mobile carousel
  useEffect(() => {
    if (!isMobile || !servicesRef.current) return;

    const container = servicesRef.current;
    const handleScroll = () => {
      const slideWidth = window.innerWidth * 0.85; // 85vw
      const newSlide = Math.round(container.scrollLeft / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMobile, currentSlide]);

  // Update the mobile services container to handle touch events
  const MobileHeroSection = () => (
    <div 
      className={`min-h-[100dvh] w-full overflow-hidden ${
        theme === 'dark' 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <div className="flex flex-col min-h-[100dvh] justify-between">
        {/* Mobile Header */}
        <div className="flex-1 px-4 pt-12 pb-6 flex flex-col items-center text-center">
          <div className="space-y-6 max-w-[90%] mx-auto">
            <h2 className={`uppercase tracking-wider text-xs font-semibold ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            }`}>
              {hero.subtitle}
            </h2>
            <h1 className={`text-2xl sm:text-3xl font-bold leading-tight ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {hero.title}
            </h1>
            <p className={`text-sm leading-relaxed max-w-xs mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {hero.description}
            </p>
          </div>

          {/* Mobile CTA Buttons */}
          <div className="flex flex-col gap-3 mt-8 w-full max-w-xs mx-auto">
            <Link
              to={hero.ctaButtons.primary.link}
              className={`w-full py-3.5 rounded-lg text-sm font-semibold text-center transition-all active:scale-95 ${
                theme === 'dark' 
                  ? 'bg-blue-600 text-white active:bg-blue-700 shadow-lg'
                  : 'bg-blue-500 text-white active:bg-blue-600 shadow-lg'
              }`}
            >
              {hero.ctaButtons.primary.text}
            </Link>
            <Link
              to={hero.ctaButtons.secondary.link}
              className={`w-full py-3.5 rounded-lg text-sm font-semibold text-center transition-all active:scale-95 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-white active:bg-gray-600 shadow-lg'
                  : 'bg-gray-800 text-white active:bg-gray-700 shadow-lg'
              }`}
            >
              {hero.ctaButtons.secondary.text}
            </Link>
          </div>

          {/* Mobile Tools Section */}
          <div className="mt-12 w-full max-w-sm mx-auto text-center">
            <h3 className={`text-sm font-semibold mb-4 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            }`}>
              {hero.toolsSection.title}
            </h3>
            <div className="flex flex-wrap justify-center gap-1.5">
              {hero.toolsSection.tools.map((toolCategory, index) => (
                <React.Fragment key={index}>
                  {toolCategory.items.map((tool, toolIndex) => (
                    <span
                      key={`${index}-${toolIndex}`}
                      className={`px-2 py-1 text-xs rounded-full transition-all active:scale-95 ${
                        theme === 'dark'
                          ? 'bg-blue-900/20 text-blue-300 border border-blue-800/30'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}
                    >
                      {tool}
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
            
        {/* Mobile Services Carousel */}
        <div className="w-full pb-8">
          <div
            ref={servicesRef}
            className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onTouchStart={() => setIsAutoPlaying(false)}
            onTouchEnd={() => setTimeout(() => setIsAutoPlaying(true), 5000)}
          >
            {services.items.map((service, index) => (
              <div
                key={index}
                className="flex-none w-[85vw] mr-4 snap-center"
              >
                <div className={`p-4 rounded-xl h-[300px] transition-all ${
                  theme === 'dark'
                    ? 'bg-gray-900/90 border border-gray-800'
                    : 'bg-white shadow-lg border border-blue-100'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                      theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon} />
                      </svg>
                    </div>
                    <h3 className={`text-base font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {service.name}
                    </h3>
                  </div>
                  <ul className={`text-xs space-y-1.5 overflow-y-auto max-h-[120px] scrollbar-hide ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <span className="mr-1.5">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slide Indicators */}
          <div className="flex justify-center mt-4 gap-1.5">
            {Array.from({ length: services.items.length }).map((_, index) => (
              <button
                key={index}
                onClick={() => handleMobileSlideClick(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  currentSlide === index 
                    ? theme === 'dark' ? 'bg-blue-500 w-3' : 'bg-blue-600 w-3'
                    : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Return conditional render
  return isMobile ? <MobileHeroSection /> : (
    <div 
      ref={sliderRef} 
      className={`min-h-screen w-full overflow-hidden flex flex-nowrap ${
        theme === 'dark' 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white' 
      }`} 
      id="slider"
    >
      {/* Main Hero Section */}
      <div className="flex-none w-full flex flex-col items-center justify-center p-6 text-center pt-12">
        <div className="max-w-8xl mx-auto space-y-8">
          <div className="space-y-4">
            <h2 className={`uppercase tracking-wider font-semibold pt-16 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600' 
            }`}>
              {hero.subtitle}
            </h2>
            <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold leading-tight ${
              theme === 'dark' ? 'text-white' :'text-gray-900' 
            }`}>
              {hero.title}
            </h1>
            <p className={`text-xl mt-4 max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {hero.description}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 mb-24 pb-16">
            <Link
              to={hero.ctaButtons.primary.link}
              className={`inline-block px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${
                theme === 'dark' 
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {hero.ctaButtons.primary.text}
            </Link>
            <Link
              to={hero.ctaButtons.secondary.link}
              className={`inline-block px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              {hero.ctaButtons.secondary.text}
            </Link>
          </div>
            
          {/* Tools & Technologies Section */}
          <motion.div 
            className="text-center mb-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            <motion.h3 
              className={`text-xl font-semibold mb-4 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut"
                  }
                }
              }}
            >
              {hero.toolsSection.title}
            </motion.h3>
            <motion.div 
              className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto px-4"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05
                  }
                }
              }}
            >
              {hero.toolsSection.tools.map((toolCategory, index) => (
                <React.Fragment key={index}>
                  {toolCategory.items.map((tool, toolIndex) => (
                    <motion.span
                      key={`${index}-${toolIndex}`}
                      className={`px-3 py-1 text-sm border rounded-full transition-colors ${
                        theme === 'dark'
                          ? 'bg-blue-900/20 text-blue-300 border-blue-800/30 hover:bg-blue-800/30'
                          : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                      }`}
                      variants={{
                        hidden: { opacity: 0, scale: 0.8 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                            duration: 0.3,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {tool}
                    </motion.span>
                  ))}
                </React.Fragment>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Services Carousel */}
          <div className="relative mt-16 pt-16">
            {/* Navigation Arrows */}
            <button
              onClick={() => scrollServices('left')}
              className={`absolute left-0 top-0 bottom-0 z-10 px-4 flex items-center justify-center transition-opacity duration-300 ${
                showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              style={{
                background: theme === 'dark' 
                  ? 'linear-gradient(to right, rgba(17, 24, 39, 0.9), transparent)'
                  : 'linear-gradient(to right, rgba(255, 255, 255, 0.9), transparent)',
                width: '80px'
              }}
            >
              <svg className={`w-10 h-10 transition-colors ${
                theme === 'dark' 
                  ? 'text-white hover:text-blue-400'
                  : 'text-gray-600 hover:text-blue-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={() => scrollServices('right')}
              className={`absolute right-0 top-0 bottom-0 z-10 px-4 flex items-center justify-center transition-opacity duration-300 ${
                showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              style={{
                background: theme === 'dark' 
                  ? 'linear-gradient(to left, rgba(17, 24, 39, 0.9), transparent)'
                  : 'linear-gradient(to left, rgba(255, 255, 255, 0.9), transparent)',
                width: '80px'
              }}
            >
              <svg className={`w-10 h-10 transition-colors ${
                theme === 'dark' 
                  ? 'text-white hover:text-blue-400'
                  : 'text-gray-600 hover:text-blue-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Services Container */}
            <div
              ref={servicesRef}
              className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {services.items.map((service, index) => (
                <div
                  key={index}
                  className="flex-none w-full md:w-1/2 lg:w-1/4 p-3 snap-start"
                >
                  <div className={`h-[300px] space-y-2 p-4 rounded-xl transition-all duration-300 transform hover:scale-105 backdrop-blur-sm ${
                    theme === 'dark'
                      ? 'hover:bg-blue-900/10 border border-blue-900/20 bg-opacity-20 bg-gray-900'
                      : 'hover:bg-blue-50 border border-blue-200/20 bg-white shadow-lg'
                  }`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg backdrop-blur-sm ${
                      theme === 'dark' ? 'bg-blue-600/20' : 'bg-blue-100'
                    }`}>
                      <svg className={`w-6 h-6 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon} />
                      </svg>
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {service.name}
                    </h3>
                    <ul className={`text-sm space-y-1.5 overflow-y-auto max-h-[180px] scrollbar-hide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="opacity-80 hover:opacity-100 transition-opacity">
                          • {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Slide Indicators */}
            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: Math.ceil(services.items.length / slidesToShow) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? theme === 'dark' ? 'bg-blue-500 w-4' : 'bg-blue-600 w-4'
                      : theme === 'dark' ? 'bg-gray-400 opacity-50 hover:opacity-75' : 'bg-gray-500 opacity-50 hover:opacity-75'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;